import { useState, useEffect } from "react";
import { Zap, Lock, User, ArrowRight, Loader2, AlertCircle } from "lucide-react";
import { Wordmark } from "@/components/wordmark";

interface SaasAdminLoginProps {
  onLogin: (admin: { id: number; username: string; displayName: string; role: string }) => void;
}

export default function SaasAdminLogin({ onLogin }: SaasAdminLoginProps) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [isBootstrap, setIsBootstrap] = useState(false);
  const [displayName, setDisplayName] = useState("");
  const [hasAdmin, setHasAdmin] = useState<boolean | null>(null);

  useEffect(() => {
    fetch("/api/saas-admin/bootstrap-check")
      .then(r => r.json())
      .then(data => setHasAdmin(data.hasAdmin))
      .catch(() => setHasAdmin(true));
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const endpoint = isBootstrap ? "/api/saas-admin/bootstrap" : "/api/saas-admin/login";
      const body = isBootstrap
        ? { username, password, displayName: displayName || "Admin" }
        : { username, password };
      const res = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
        credentials: "include",
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.message || "Login failed");
      }
      const admin = await res.json();
      if (isBootstrap) {
        const loginRes = await fetch("/api/saas-admin/login", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ username, password }),
          credentials: "include",
        });
        if (loginRes.ok) {
          const loggedIn = await loginRes.json();
          onLogin(loggedIn);
          return;
        }
      }
      onLogin(admin);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6" data-testid="saas-admin-login-page">
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center mb-10">
          <div className="mb-3">
            <Wordmark fs={34} />
          </div>
          <p className="text-foreground/30 text-sm font-medium tracking-wide">SaaS Admin Console</p>
        </div>

        <div className="bg-foreground/[0.03] border border-foreground/[0.06] rounded-2xl p-8">
          <h2 className="text-xl font-display font-bold text-foreground mb-1">
            {isBootstrap ? "Create Admin Account" : "Sign In"}
          </h2>
          <p className="text-foreground/30 text-sm mb-6">
            {isBootstrap ? "Set up your admin credentials" : "Access your business dashboard"}
          </p>

          {error && (
            <div className="flex items-center gap-2 px-4 py-3 mb-6 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm" data-testid="text-login-error">
              <AlertCircle size={16} />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleLogin} className="space-y-4">
            {isBootstrap && (
              <div>
                <label className="block text-xs font-semibold text-foreground/40 uppercase tracking-wider mb-2">Display Name</label>
                <div className="relative">
                  <User size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-foreground/20" />
                  <input
                    type="text"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    placeholder="Your name"
                    className="w-full pl-11 pr-4 py-3 bg-foreground/[0.04] border border-foreground/[0.08] rounded-xl text-foreground placeholder-white/20 focus:outline-none focus:border-blue-500/40 transition-colors text-sm"
                    data-testid="input-display-name"
                  />
                </div>
              </div>
            )}

            <div>
              <label className="block text-xs font-semibold text-foreground/40 uppercase tracking-wider mb-2">Username</label>
              <div className="relative">
                <User size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-foreground/20" />
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="admin"
                  required
                  className="w-full pl-11 pr-4 py-3 bg-foreground/[0.04] border border-foreground/[0.08] rounded-xl text-foreground placeholder-white/20 focus:outline-none focus:border-blue-500/40 transition-colors text-sm"
                  data-testid="input-username"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-foreground/40 uppercase tracking-wider mb-2">Password</label>
              <div className="relative">
                <Lock size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-foreground/20" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter password"
                  required
                  className="w-full pl-11 pr-4 py-3 bg-foreground/[0.04] border border-foreground/[0.08] rounded-xl text-foreground placeholder-white/20 focus:outline-none focus:border-blue-500/40 transition-colors text-sm"
                  data-testid="input-password"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 py-3.5 bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 text-foreground font-bold rounded-xl transition-all text-sm disabled:opacity-50"
              data-testid="button-login-submit"
            >
              {loading ? <Loader2 size={18} className="animate-spin" /> : <ArrowRight size={18} />}
              <span>{isBootstrap ? "Create Account" : "Sign In"}</span>
            </button>
          </form>

          {hasAdmin === false && (
            <button
              onClick={() => { setIsBootstrap(!isBootstrap); setError(""); }}
              className="w-full mt-4 text-center text-xs text-foreground/25 hover:text-foreground/50 transition-colors py-2"
              data-testid="button-toggle-bootstrap"
            >
              {isBootstrap ? "Already have an account? Sign in" : "First time? Create admin account"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
